import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { Dispatch, MutableRefObject, SetStateAction } from "react";
import { TaggedUserType } from "../../../slices/TicketSlice";
import { IMention } from "../../../types/User/IMention";
import { CallDescToTaggedDescriptionConverter } from "./utils";
import { EditorChangeEvent } from "@progress/kendo-react-editor";
import { TextAreaChangeEvent } from "@progress/kendo-react-inputs";

export interface IMentionsHandle {
  dispatch: ThunkDispatch<any, any, any>;
  SetDescription(action: string): AnyAction;
  SetTaggedDescription(action: string): AnyAction;
  SetDescription(action: string): AnyAction;
  setAtActivated: Dispatch<SetStateAction<number>>;
  setMentionsText: Dispatch<SetStateAction<string>>;
  TaggedUsers: TaggedUserType[];
}

export interface IMentionsHandleOnClick extends IMentionsHandle {
  SetTaggedUsers(action: TaggedUserType[]): AnyAction;
  textAreaRef: MutableRefObject<any>;
  mentionsText: string;
}

export function DefaultMentionsHandleOnClick(
  person: IMention,
  required: IMentionsHandleOnClick
) {
  const {
    dispatch,
    SetDescription,
    SetTaggedDescription,
    SetTaggedUsers,
    TaggedUsers,
    setAtActivated,
    setMentionsText,
    mentionsText,
    textAreaRef,
  } = required;
  setAtActivated(-1);
  setMentionsText("");
  const idx = textAreaRef.current.element.current.selectionStart;

  const whole = textAreaRef.current.value;
  const prefixCallDesc = whole.substring(0, idx - mentionsText.length);
  const suffixCallDesc = whole.substring(idx);
  const additionCallDesc = `[[@${person.label}]]`;
  const newCallDesc = prefixCallDesc + additionCallDesc + suffixCallDesc;
  dispatch(SetDescription(newCallDesc));

  const newTag: TaggedUserType = { label: person.label, value: person.value };
  const newTaggedUsers: TaggedUserType[] = [...TaggedUsers, newTag];
  dispatch(SetTaggedUsers(newTaggedUsers));

  const newTaggedDescription = CallDescToTaggedDescriptionConverter(
    newCallDesc,
    newTaggedUsers
  );
  dispatch(SetTaggedDescription(newTaggedDescription));
  textAreaRef.current.element.current.focus()
}

export interface IMentionsHandleOnChange extends IMentionsHandle {
  minChars: string;
  atActivated: number;
}

export function MentionsHandleOnChange(
  e: EditorChangeEvent | TextAreaChangeEvent,
  eType: string,
  required: IMentionsHandleOnChange
) {
  const {
    dispatch,
    SetDescription,
    SetTaggedDescription,
    TaggedUsers,
    setAtActivated,
    setMentionsText,
    atActivated,
    minChars,
  } = required;
  let val: string;
  let newE: EditorChangeEvent | TextAreaChangeEvent;
  switch (eType) {
    case "TextAreaChangeEvent":
      newE = e as TextAreaChangeEvent;
      val = newE.target.value;
      break;
    case "EditorChangeEvent":
      newE = e as EditorChangeEvent;
      val = newE.html;
      break;
  }

  dispatch(SetDescription(val));

  const newTaggedDescription = CallDescToTaggedDescriptionConverter(
    val,
    TaggedUsers
  );
  dispatch(SetTaggedDescription(newTaggedDescription));
  // captures the form @name limited to max five characters with custom min characters.
  // const atRegex = new RegExp(
  //   `^@\\w{${minChars},5}|([^[]@\\w{${minChars},5})`,
  //   "m"
  // );
  // const maxRegex = new RegExp(`^@\\w{6}|([^[]@\\w{6})`, "m");
  const atRegex = new RegExp(
    `^@\\w{${minChars},6}|[^[]@\\w{${minChars},6}`,
    "m"
  );
  const maxRegex = new RegExp(`^@\\w{6,}|[^[]@\\w{6,}`, "m");
  const spaceBreakRegex = new RegExp("^@\\w+\\s|[^[]@\\w+\\s", "m");
  // console.log("atRegex source: ", atRegex.source);
  // console.log("maxRegex source: ", maxRegex.source);
  // console.log("maxRegex: ", maxRegex.test(val));
  // console.log("atRegex: ", atRegex.test(val));
  // console.log("atActivated: ", atActivated);

  const atRegexMatch = atRegex.test(val);
  const maxRegexMatch = maxRegex.test(val);
  const spaceBreakMatch = spaceBreakRegex.test(val);

  if (
    atRegexMatch &&
    !maxRegexMatch &&
    !spaceBreakMatch &&
    atActivated === -1
  ) {
    setAtActivated(1);
  } else if (
    (!atRegexMatch || maxRegexMatch || spaceBreakMatch || val.length === 0) &&
    atActivated >= 0
  ) {
    // reset
    setAtActivated(-1);
    setMentionsText("");
    // console.log("reset atActivated");
  } else if (atActivated >= 0) {
    const valMatch = val.match(atRegex);
    // const setActive =
    //   valMatch[0].slice(0, 1) === " " ? valMatch.index + 1 : valMatch.index;
    const setActive = valMatch.index + 1;
    const res = valMatch[0].trim().replace(">", "");
    setAtActivated(setActive);
    setMentionsText(res);

    // console.log("setActive: ", `"${setActive}"`);
    // console.log(
    //   "replace at: ",
    //   `"${val.slice(setActive, setActive + res.length)}"`
    // );
    // console.log("res: ", `"${res}"`);
  }
}

export interface IMentionsHandleOnClickForTextEditor extends IMentionsHandle {
  atActivated: number;
  SetTaggedUsers(action: TaggedUserType[]): AnyAction;
  mentionsText: string;
  HTML_Notes: string;
}
export function MentionsHandleOnClickForTextEditor(
  person: IMention,
  required: IMentionsHandleOnClickForTextEditor
) {
  const {
    dispatch,
    SetDescription,
    SetTaggedDescription,
    SetTaggedUsers,
    TaggedUsers,
    setAtActivated,
    atActivated,
    setMentionsText,
    mentionsText,
    HTML_Notes,
  } = required;
  setAtActivated(-1);
  setMentionsText("");

  const endOfString = atActivated + mentionsText.length;
  const prefixHTML_Notes = HTML_Notes.slice(0, atActivated);
  const suffixHTML_Notes = HTML_Notes.substring(endOfString).replace(
    "<p></p>",
    ""
  );
  const additionHTML_Notes = `[[@${person.label}]]`;
  const newHTML_Notes =
    prefixHTML_Notes + additionHTML_Notes + suffixHTML_Notes;
  dispatch(SetDescription(newHTML_Notes));

  const newTag: TaggedUserType = { label: person.label, value: person.value };
  const newTaggedUsers: TaggedUserType[] = [...TaggedUsers, newTag];
  dispatch(SetTaggedUsers(newTaggedUsers));

  const newTaggedDescription = CallDescToTaggedDescriptionConverter(
    newHTML_Notes,
    newTaggedUsers
  );
  dispatch(SetTaggedDescription(newTaggedDescription));
  const iframe = document.querySelector("div.k-editor-content iframe");
  //@ts-ignore
  const div = iframe.contentDocument.querySelector("div") as HTMLElement;
  div.focus();

  // console.log("atActivated: ", atActivated);
  // console.log("HTML_Notes: ", HTML_Notes);
  // console.log("endOfString: ", endOfString);
  // console.log("prefixHTML_Notes: ", prefixHTML_Notes);
  // console.log("suffixHTML_Notes: ", suffixHTML_Notes);
  // console.log("newHTML_Notes: ", newHTML_Notes);
  // console.log("newTaggedDescription: ", newTaggedDescription);

  // console.log("iframe: ", iframe);
  // console.log("div: ", div);
}
